import { Link, useNavigate } from 'react-router-dom';
import {
    Sparkles,
    Zap,
    ArrowRight,
    CheckCircle,
    Code,
    Bug,
    TestTube2,
    GitBranch,
    FileCode,
    Terminal,
    Database,
    Cpu,
    ChevronDown,
    Megaphone,
    Briefcase,
    Star,
    Shield,
    Clock,
    Rocket
} from 'lucide-react';
import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { modulesApi } from '../../utils/api';
import { useAuthStore } from '../../store/auth';

export default function DevAI() {
    const navigate = useNavigate();
    const { isAuthenticated } = useAuthStore();
    const [productsOpen, setProductsOpen] = useState(false);
    const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('monthly');
    const [purchasing, setPurchasing] = useState(false);

    // Purchase bundle mutation
    const purchaseMutation = useMutation({
        mutationFn: () => modulesApi.purchaseBundle('bundle_developer', billingPeriod),
        onSuccess: (response) => {
            if (response.data?.data?.paymentUrl) {
                window.location.href = response.data.data.paymentUrl;
            }
        },
        onError: (error: any) => {
            console.error('Purchase failed:', error);
            alert(error.response?.data?.message || 'Purchase failed. Please try again.');
            setPurchasing(false);
        }
    });

    const handlePurchase = () => {
        if (!isAuthenticated) {
            navigate('/register?redirect=/products/devai&action=buy');
            return;
        }
        setPurchasing(true);
        purchaseMutation.mutate();
    };

    const features = [
        {
            icon: Code,
            title: 'Code Generation',
            description: 'Generate production-ready code in any language with AI. From boilerplate to complex algorithms.',
            tools: ['generate_code', 'scaffold_project', 'convert_language', 'explain_code']
        },
        {
            icon: Bug,
            title: 'Smart Debugging',
            description: 'AI-powered bug detection, root cause analysis, and suggested fixes with explanations.',
            tools: ['detect_bugs', 'analyze_stacktrace', 'suggest_fix', 'security_scan']
        },
        {
            icon: TestTube2,
            title: 'Test Generation',
            description: 'Generate comprehensive unit tests, integration tests, and E2E tests automatically.',
            tools: ['generate_unit_test', 'generate_integration_test', 'generate_e2e_test', 'coverage_analysis']
        },
        {
            icon: FileCode,
            title: 'Code Review',
            description: 'Get instant AI code reviews with suggestions for improvements and best practices.',
            tools: ['review_code', 'suggest_refactor', 'check_style', 'detect_code_smells']
        },
        {
            icon: GitBranch,
            title: 'Git Automation',
            description: 'AI-generated commit messages, PR descriptions, and changelog updates.',
            tools: ['generate_commit_message', 'generate_pr_description', 'generate_changelog', 'review_pr']
        },
        {
            icon: Terminal,
            title: 'CLI Generator',
            description: 'Generate CLI tools, shell scripts, and automation workflows with natural language.',
            tools: ['generate_cli', 'generate_script', 'cron_builder', 'docker_compose_generate']
        },
        {
            icon: Database,
            title: 'Database Tools',
            description: 'Generate SQL queries, migrations, schema designs, and data transformations.',
            tools: ['generate_sql', 'generate_migration', 'design_schema', 'optimize_query']
        },
        {
            icon: Cpu,
            title: 'API Builder',
            description: 'Generate REST/GraphQL APIs, OpenAPI specs, and API documentation automatically.',
            tools: ['generate_api', 'generate_openapi', 'generate_graphql', 'generate_api_docs']
        }
    ];

    const benefits = [
        { icon: Rocket, title: '10x Faster', description: 'Write code 10x faster with AI assistance' },
        { icon: Shield, title: 'Fewer Bugs', description: 'Catch issues before they reach production' },
        { icon: Star, title: 'Best Practices', description: 'AI enforces coding standards automatically' },
        { icon: Clock, title: 'Save Hours', description: 'Automate repetitive coding tasks' }
    ];

    return (
        <div className="min-h-screen bg-dark-950">
            {/* Navigation */}
            <nav className="fixed top-0 left-0 right-0 z-50 bg-dark-950/80 backdrop-blur-lg border-b border-dark-800">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <Link to="/" className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-primary-700 rounded-xl flex items-center justify-center">
                                <Sparkles className="w-6 h-6 text-white" />
                            </div>
                            <span className="text-xl font-bold">aiOps</span>
                        </Link>
                        <div className="hidden md:flex items-center gap-8">
                            <div className="relative">
                                <button
                                    onClick={() => setProductsOpen(!productsOpen)}
                                    onBlur={() => setTimeout(() => setProductsOpen(false), 150)}
                                    className="flex items-center gap-1 text-white transition"
                                >
                                    Products
                                    <ChevronDown className={`w-4 h-4 transition-transform ${productsOpen ? 'rotate-180' : ''}`} />
                                </button>
                                {productsOpen && (
                                    <div className="absolute top-full left-0 mt-2 w-64 bg-dark-900 border border-dark-700 rounded-xl shadow-xl overflow-hidden">
                                        <Link
                                            to="/products/marketing-automation"
                                            className="flex items-center gap-3 px-4 py-3 hover:bg-dark-800 transition"
                                        >
                                            <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-red-500 rounded-lg flex items-center justify-center">
                                                <Megaphone className="w-5 h-5 text-white" />
                                            </div>
                                            <div>
                                                <div className="font-semibold text-white">Marketing Automation</div>
                                                <div className="text-xs text-dark-400">AI-Powered Digital Marketing</div>
                                            </div>
                                        </Link>
                                        <Link
                                            to="/products/devai"
                                            className="flex items-center gap-3 px-4 py-3 bg-dark-800 transition border-t border-dark-700"
                                        >
                                            <div className="w-10 h-10 bg-gradient-to-br from-cyan-500 to-blue-500 rounded-lg flex items-center justify-center">
                                                <Code className="w-5 h-5 text-white" />
                                            </div>
                                            <div>
                                                <div className="font-semibold text-white">DevAI</div>
                                                <div className="text-xs text-dark-400">Developer AI Tools</div>
                                            </div>
                                        </Link>
                                        <Link
                                            to="/products/bizops"
                                            className="flex items-center gap-3 px-4 py-3 hover:bg-dark-800 transition border-t border-dark-700"
                                        >
                                            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-violet-500 rounded-lg flex items-center justify-center">
                                                <Briefcase className="w-5 h-5 text-white" />
                                            </div>
                                            <div>
                                                <div className="font-semibold text-white">BizOps</div>
                                                <div className="text-xs text-dark-400">Business Operations AI</div>
                                            </div>
                                        </Link>
                                    </div>
                                )}
                            </div>
                            <Link to="/pricing" className="text-dark-300 hover:text-white transition">Pricing</Link>
                        </div>
                        <div className="flex items-center gap-4">
                            {isAuthenticated ? (
                                <Link to="/dashboard" className="btn btn-primary">
                                    Dashboard
                                </Link>
                            ) : (
                                <>
                                    <Link to="/login" className="text-dark-300 hover:text-white transition">Sign In</Link>
                                    <Link to="/register" className="btn btn-primary">Get Started</Link>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <section className="pt-32 pb-20 px-4">
                <div className="max-w-7xl mx-auto text-center">
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-cyan-500/10 border border-cyan-500/20 rounded-full text-cyan-400 text-sm mb-6">
                        <Code className="w-4 h-4" />
                        Developer AI Tools
                    </div>
                    <h1 className="text-5xl md:text-7xl font-bold mb-6">
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 via-blue-500 to-purple-500">
                            Code Smarter,
                        </span>
                        <br />
                        <span className="text-white">Ship Faster</span>
                    </h1>
                    <p className="text-xl text-dark-300 max-w-3xl mx-auto mb-8">
                        AI-powered development tools that help you write better code, find bugs faster,
                        and automate repetitive tasks. Built for modern development workflows.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <button
                            onClick={handlePurchase}
                            disabled={purchasing}
                            className="btn btn-primary text-lg px-8 py-4 flex items-center gap-2"
                        >
                            {purchasing ? 'Processing...' : 'Start Free Trial'}
                            <ArrowRight className="w-5 h-5" />
                        </button>
                        <Link to="/docs" className="btn btn-secondary text-lg px-8 py-4">
                            View Documentation
                        </Link>
                    </div>
                </div>
            </section>

            {/* Benefits */}
            <section className="py-16 px-4 border-t border-dark-800">
                <div className="max-w-7xl mx-auto">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                        {benefits.map((benefit) => (
                            <div key={benefit.title} className="text-center">
                                <div className="w-16 h-16 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                    <benefit.icon className="w-8 h-8 text-cyan-400" />
                                </div>
                                <h3 className="text-lg font-semibold mb-2">{benefit.title}</h3>
                                <p className="text-dark-400 text-sm">{benefit.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Features Grid */}
            <section className="py-20 px-4">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl font-bold mb-4">Developer AI Modules</h2>
                        <p className="text-dark-400 text-lg max-w-2xl mx-auto">
                            Everything you need to accelerate your development workflow
                        </p>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
                        {features.map((feature) => (
                            <div
                                key={feature.title}
                                className="bg-dark-900/50 border border-dark-800 rounded-2xl p-6 hover:border-cyan-500/50 transition-colors group"
                            >
                                <div className="w-12 h-12 bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-xl flex items-center justify-center mb-4 group-hover:from-cyan-500/30 group-hover:to-blue-500/30 transition-colors">
                                    <feature.icon className="w-6 h-6 text-cyan-400" />
                                </div>
                                <h3 className="text-lg font-semibold mb-2">{feature.title}</h3>
                                <p className="text-dark-400 text-sm mb-4">{feature.description}</p>
                                <div className="flex flex-wrap gap-1">
                                    {feature.tools.slice(0, 2).map((tool) => (
                                        <span key={tool} className="text-xs bg-dark-800 px-2 py-1 rounded text-dark-400">
                                            {tool}
                                        </span>
                                    ))}
                                    {feature.tools.length > 2 && (
                                        <span className="text-xs text-dark-500">+{feature.tools.length - 2} more</span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Pricing CTA */}
            <section className="py-20 px-4 bg-gradient-to-b from-dark-950 to-dark-900">
                <div className="max-w-4xl mx-auto text-center">
                    <h2 className="text-4xl font-bold mb-4">Ready to Code Smarter?</h2>
                    <p className="text-dark-400 text-lg mb-8">
                        Start your 14-day free trial. No credit card required.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <button
                            onClick={handlePurchase}
                            disabled={purchasing}
                            className="btn btn-primary text-lg px-8 py-4"
                        >
                            Start Free Trial
                        </button>
                        <Link to="/pricing" className="btn btn-secondary text-lg px-8 py-4">
                            View Pricing
                        </Link>
                    </div>
                    <p className="text-dark-500 text-sm mt-4">
                        Developer Suite: $39/month or $374/year (20% off)
                    </p>
                </div>
            </section>

            {/* Footer */}
            <footer className="py-12 px-4 border-t border-dark-800">
                <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-gradient-to-br from-primary-500 to-primary-700 rounded-lg flex items-center justify-center">
                            <Sparkles className="w-4 h-4 text-white" />
                        </div>
                        <span className="font-semibold">aiOps</span>
                    </div>
                    <div className="text-dark-400 text-sm">
                        © 2026 aiOps. All rights reserved.
                    </div>
                </div>
            </footer>
        </div>
    );
}
